import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { format, parseISO, isAfter } from "date-fns";
import { Menu } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/components/ui/use-toast";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";
import MenuBar from "@/components/MenuBar";

import TimeOffSection from "./TimeOffSection";
import ExpensesSection from "./ExpensesSection";
import HoursSection from "./HoursSection";
import {
  ApiResponse,
  EarningRate,
  EarningRatesResponse,
  LeavePoliciesResponse,
  LeavePolicyBalance,
  SubmissionResponse,
} from "@/components/submission/types";
import { CustomRate, EmployeeEarningsResponse } from "@/types/earnings";
import { Expense, HoursWorked, TimeOffRequest } from "@/types/nanny";

const NannySubmission: React.FC = () => {
  const { submissionId } = useParams<{ submissionId: string }>();
  const { toast } = useToast();

  const [submission, setSubmission] = useState<SubmissionResponse | null>(null);
  const [leaveTypes, setLeaveTypes] = useState<LeavePolicyBalance[]>([]);
  const [earningRates, setEarningRates] = useState<CustomRate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isReopening, setIsReopening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLatestSubmission, setIsLatestSubmission] = useState<boolean>(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!submissionId) return;

      try {
        setIsLoading(true);

        // First, get all submissions to check if this is the latest one
        const allSubmissions = await ApiService.request<SubmissionResponse[]>(
          EndpointKeys.GET_EMPLOYEE_SUBMISSIONS
        );
        
        // Find the latest submission date
        if (allSubmissions.length > 0) {
          const dates = allSubmissions.map(sub => parseISO(sub.submission_month));
          const latestDate = dates.reduce((latest, current) => 
            isAfter(current, latest) ? current : latest
          );
          
          // Check if current submission is the latest one
          const currentSubmissionId = Number(submissionId);
          const latestSubmission = allSubmissions.find(sub => {
            const subDate = parseISO(sub.submission_month);
            return subDate.getTime() === latestDate.getTime() && sub.status === "REJECTED";
          });
          
          setIsLatestSubmission(
            latestSubmission !== undefined && latestSubmission.id === currentSubmissionId
          );
        }

        const [submissionData, leaveTypesData, earningRatesData] =
          await Promise.all([
            ApiService.request<ApiResponse<SubmissionResponse>>(
              EndpointKeys.GET_SUBMISSION,
              {
                urlParams: { id: Number(submissionId) },
              },
            ),
            ApiService.request<LeavePoliciesResponse>(
              EndpointKeys.FETCH_LEAVE_POLICIES,
              {
                queryParams: {
                  submissionId: submission?.id || submissionId,
                },
              },
            ),
            ApiService.request<EmployeeEarningsResponse>(
              EndpointKeys.FETCH_EMPLOYEE_EARNINGS,
            ),
          ]);

        setSubmission(submissionData.data);
        setLeaveTypes(leaveTypesData.leavePolicies);
        setEarningRates(earningRatesData.employees[0].customRates);
      } catch (err) {
        console.log(err);
        setError(err instanceof Error ? err.message : "An error occurred");
        toast({
          title: "Error",
          description: "Failed to load submission data",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [submissionId, toast]);

  const handleUpdateTimeOff = (updatedTimeOff: TimeOffRequest[]) => {
    if (submission) {
      setSubmission({
        ...submission,
        time_off_requests: updatedTimeOff,
      });
    }
  };

  const handleUpdateHours = (updatedHours: HoursWorked[]) => {
    if (submission) {
      setSubmission({
        ...submission,
        hours_worked: updatedHours,
      });
    }
  };

  const handleUpdateExpenses = (updatedExpenses: Expense[]) => {
    if (submission) {
      setSubmission({
        ...submission,
        expenses: updatedExpenses,
      });
    }
  };

  const handleSubmit = async () => {
    if (!submissionId) return;

    try {
      setIsSubmitting(true);

      const response = await ApiService.request<
        ApiResponse<SubmissionResponse>
      >(EndpointKeys.SUBMIT_SUBMISSION, {
        method: "POST",
        urlParams: { id: Number(submissionId) },
      });

      setSubmission(response.data);

      toast({
        title: "Success",
        description: "Monthly submission has been sent for approval",
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to submit");
      toast({
        title: "Error",
        description: "Failed to submit monthly submission",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReopen = async () => {
    if (!submissionId) return;

    try {
      setIsReopening(true);
      
      const response = await ApiService.request<
        ApiResponse<SubmissionResponse>
      >(EndpointKeys.REOPEN_SUBMISSION, {
        method: "POST",
        urlParams: { id: Number(submissionId) },
      });

      if (response.success) {
        // Fetch all data again to get updated submission with reopened items
        const [submissionData, leaveTypesData, earningRatesData] =
          await Promise.all([
            ApiService.request<ApiResponse<SubmissionResponse>>(
              EndpointKeys.GET_SUBMISSION,
              {
                urlParams: { id: Number(submissionId) },
              },
            ),
            ApiService.request<LeavePoliciesResponse>(
              EndpointKeys.FETCH_LEAVE_POLICIES,
              {
                queryParams: {
                  submissionId: submissionId,
                },
              },
            ),
            ApiService.request<EmployeeEarningsResponse>(
              EndpointKeys.FETCH_EMPLOYEE_EARNINGS,
            ),
          ]);
        
        setSubmission(submissionData.data);
        setLeaveTypes(leaveTypesData.leavePolicies);
        setEarningRates(earningRatesData.employees[0].customRates);

        toast({
          title: "Success", 
          description: "Submission has been reopened for editing. All rejected items have been restored to pending status."
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to reopen submission");
      toast({
        title: "Error",
        description: "Failed to reopen submission",
        variant: "destructive",
      });
    } finally {
      setIsReopening(false);
    }
  };
  
  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!submission) {
    return <div>No submission found</div>;
  }

  const canEdit = submission.status === "DRAFT";
  const isRejected = submission.status === "REJECTED";
  const submissionMonth = format(
    parseISO(submission.submission_month),
    "MMMM yyyy",
  );

  return (
    <div className="flex min-h-screen bg-background">
      {/* Mobile Menu Toggle */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-30 flex items-center p-4 bg-gray-900">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="text-white"
        >
          <Menu className="h-6 w-6" />
        </Button>
      </div>

      {/* Sidebar */}
      <div
        className={`${
          isMobileMenuOpen ? "block" : "hidden"
        } md:block fixed md:relative w-64 h-full bg-gray-900 z-20`}
      >
        <MenuBar />
      </div>

      {/* Main Content */}
      <div className="flex-1 w-full md:w-auto mt-16 md:mt-0 overflow-auto">
        <div className="p-4 md:p-8">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-3xl font-bold">Monthly Submission</h1>
              <p className="text-muted-foreground">{submissionMonth}</p>
            </div>
            <div className="flex gap-2">
              {isRejected && isLatestSubmission && (
                <Button onClick={handleReopen} disabled={isReopening} className="bg-secondary hover:bg-secondary/90 text-secondary-foreground" data-testid="reopen-submission-button">
                  {isReopening ? "Reopening..." : "Reopen Submission"}
                </Button>
              )}
              {canEdit && (
                <Button onClick={handleSubmit} disabled={isSubmitting} data-testid="submit-submission-button">
                  Submit for Approval
                </Button>
              )}
            </div>
          </div>

          {!canEdit && !isRejected && (
            <Alert className="mb-6">
              <AlertTitle>Read Only</AlertTitle>
              <AlertDescription>
                This submission has been {submission.status.toLowerCase()} and
                cannot be edited.
              </AlertDescription>
            </Alert>
          )}
          
          {isRejected && (
            <Alert className="mb-6" variant="destructive">
              <AlertTitle>Submission Rejected</AlertTitle>
              <AlertDescription>
                {isLatestSubmission ? 
                  "This submission has been rejected. You can reopen it to make changes and resubmit." :
                  "This submission has been rejected. Since it's not the latest submission, it cannot be reopened."
                }
              </AlertDescription>
            </Alert>
          )}

          <Tabs defaultValue="hours" className="w-full">
            <TabsList className="w-full justify-start overflow-auto">
              <TabsTrigger value="hours">Hours Worked</TabsTrigger>
              <TabsTrigger value="timeoff">Time Off</TabsTrigger>
              <TabsTrigger value="expenses">Expenses</TabsTrigger>
            </TabsList>

            <TabsContent value="hours">
              <Card>
                <CardHeader>
                  <CardTitle>Hours Worked</CardTitle>
                  <CardDescription>
                    Record your working hours for different types of work
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <HoursSection
                    hoursWorked={submission.hours_worked}
                    earningRates={earningRates}
                    submissionId={submission.id}
                    isEditable={canEdit}
                    onUpdate={handleUpdateHours}
                  />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="timeoff">
              <Card>
                <CardHeader>
                  <CardTitle>Time Off Requests</CardTitle>
                  <CardDescription>
                    Submit requests for time off
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <TimeOffSection
                    timeOffRequests={submission.time_off_requests}
                    leavePolicies={leaveTypes}
                    submissionId={submission.id}
                    isEditable={canEdit}
                    onUpdate={handleUpdateTimeOff}
                  />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="expenses">
              <Card>
                <CardHeader>
                  <CardTitle>Expenses</CardTitle>
                  <CardDescription>
                    Submit expense claims with receipts
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ExpensesSection
                    expenses={submission.expenses}
                    submissionId={submission.id}
                    isEditable={canEdit}
                    onUpdate={handleUpdateExpenses}
                  />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default NannySubmission;
